import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SectionLabel } from "../components/SectionLabel";
import { PROJECTS, fetchProjects } from "../data/projects";
import { mediaUrl } from "../lib/media";

export default function Work() {
  const [projects, setProjects] = useState(PROJECTS);

  useEffect(() => {
    fetchProjects()
      .then((list) => {
        if (Array.isArray(list) && list.length) setProjects(list);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="work" data-testid="work-section" className="scroll-mt-20 bg-ink">
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-36">
        <Reveal>
          <SectionLabel index="CH.06">Selected Work</SectionLabel>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.12] tracking-tight text-paper sm:text-5xl sm:leading-[1.08] lg:text-6xl">
              Proof over
              <br />
              promises<span className="text-acid">.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="text-base leading-relaxed text-fog sm:text-lg">
              Real environments, retail programs and objects — designed, fabricated and installed
              by one team. Every project documented like it matters, because it does.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {projects.map((p, i) => (
            <Reveal key={p.slug} delay={0.08 * i} className="lg:col-span-6" y={36}>
              <Link
                to={`/work/${p.slug}`}
                data-testid={`work-card-${p.slug}`}
                className="group relative block overflow-hidden border border-white/10 bg-panel"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={mediaUrl(p.poster)}
                    alt={p.title}
                    className="aspect-[16/10] w-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent" />
                  {p.sample && (
                    <span
                      data-testid="work-card-sample-badge"
                      className="absolute left-4 top-4 bg-acid px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-ink"
                    >
                      Sample Project
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-end justify-between gap-4 p-6 sm:p-8">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-acid">
                      {p.client}{p.client && p.show ? " — " : ""}{p.show}
                    </p>
                    <h3 className="mt-3 font-display text-2xl font-bold uppercase tracking-tight text-paper sm:text-3xl">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm text-fog">{p.hook}</p>
                  </div>
                  <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-paper/60 transition-colors duration-300 group-hover:text-acid">
                    View Case Study
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}

          <Reveal
            delay={0.15}
            className={
              projects.length === 0
                ? "lg:col-span-12"
                : projects.length % 2 === 1
                  ? "lg:col-span-6"
                  : "lg:col-span-12"
            }
            y={36}
          >
            <a
              href="#contact"
              data-testid="work-next-project-card"
              className="group flex h-full min-h-[20rem] flex-col items-start justify-between border border-dashed border-white/20 p-8 transition-colors duration-500 hover:border-acid"
            >
              <span
                className="font-display text-6xl font-extrabold text-paper/15 transition-colors duration-500 group-hover:text-acid"
                aria-hidden="true"
              >
                +
              </span>
              <span>
                <span className="block font-display text-2xl font-bold uppercase tracking-tight text-paper">
                  Your next build
                </span>
                <span className="mt-3 block text-sm leading-relaxed text-fog">
                  The next case study on this page should be yours.
                </span>
                <span className="mt-5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-acid">
                  Start a Project
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </span>
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
